/**
 * Job filters component
 * Filter controls for jobs list
 */

import { useState, useEffect, useRef } from 'react';
import { Input, Select, DatePicker, Space, Button, Badge, Drawer, Form, Row, Col } from 'antd';
import {
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { JobStatus } from '@/types/job';
import { JOB_STATUS_CONFIG, DEBOUNCE } from '@/utils/constants';

const { RangePicker } = DatePicker;

export interface JobFiltersState {
  search?: string;
  status?: JobStatus[];
  dateRange?: [Dayjs, Dayjs] | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface JobFiltersProps {
  filters: JobFiltersState;
  onChange: (filters: JobFiltersState) => void;
  onClear: () => void;
  loading?: boolean;
}

export function JobFilters({ filters, onChange, onClear, loading }: JobFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Local state for search input to enable debouncing
  const [localSearchValue, setLocalSearchValue] = useState(filters.search || '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync local search value when filters change externally (e.g., clear)
  useEffect(() => {
    setLocalSearchValue(filters.search || '');
  }, [filters.search]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const activeFiltersCount = [
    filters.status?.length,
    filters.dateRange,
  ].filter(Boolean).length;

  const statusOptions = Object.entries(JOB_STATUS_CONFIG).map(([value, config]) => ({
    value,
    label: config.label,
  }));

  const sortOptions = [
    { value: 'createdAt', label: 'Created Date' },
    { value: 'updatedAt', label: 'Last Updated' },
    { value: 'searchQuery', label: 'Query Name' },
    { value: 'placesFound', label: 'Places Found' },
    { value: 'progress', label: 'Progress' },
  ];

  // Debounced search handler
  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      onChange({ ...filters, search: value || undefined });
    }, DEBOUNCE.SEARCH);
  };

  // Immediate search (for Enter key)
  const handleSearchNow = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    onChange({ ...filters, search: localSearchValue || undefined });
  };

  const handleStatusChange = (value: JobStatus[]) => {
    onChange({ ...filters, status: value.length > 0 ? value : undefined });
  };

  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    onChange({ ...filters, dateRange: dates });
  };

  const handleSortChange = (value: string) => {
    onChange({ ...filters, sortBy: value });
  };

  const handleSortOrderChange = (value: 'asc' | 'desc') => {
    onChange({ ...filters, sortOrder: value });
  };

  return (
    <>
      <Space size={12} wrap>
        {/* Search input with debouncing */}
        <Input.Search
          placeholder="Search jobs..."
          value={localSearchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onSearch={handleSearchNow}
          onPressEnter={handleSearchNow}
          style={{ width: 250 }}
          allowClear
          loading={loading}
          enterButton
        />

        {/* Status filter */}
        <Select
          mode="multiple"
          placeholder="Status"
          value={filters.status}
          onChange={handleStatusChange}
          options={statusOptions}
          style={{ minWidth: 150 }}
          maxTagCount={1}
          allowClear
        />

        {/* Sort */}
        <Space.Compact>
          <Select
            placeholder="Sort by"
            value={filters.sortBy}
            onChange={handleSortChange}
            options={sortOptions}
            style={{ width: 140 }}
            allowClear
          />
          <Select
            value={filters.sortOrder || 'desc'}
            onChange={handleSortOrderChange}
            options={[
              { value: 'desc', label: 'Newest' },
              { value: 'asc', label: 'Oldest' },
            ]}
            style={{ width: 100 }}
          />
        </Space.Compact>

        {/* More filters button */}
        <Badge count={activeFiltersCount} size="small">
          <Button
            icon={<FilterOutlined />}
            onClick={() => setDrawerOpen(true)}
          >
            More Filters
          </Button>
        </Badge>

        {/* Clear all */}
        {(filters.search || filters.status?.length || filters.dateRange) && (
          <Button
            type="text"
            icon={<ClearOutlined />}
            onClick={onClear}
          >
            Clear
          </Button>
        )}
      </Space>

      {/* Advanced filters drawer */}
      <Drawer
        title="Advanced Filters"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={
          <Button onClick={onClear} size="small">
            Clear All
          </Button>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Date Range">
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => handleDateRangeChange(dates as [Dayjs, Dayjs] | null)}
              style={{ width: '100%' }}
              presets={[
                { label: 'Today', value: [dayjs(), dayjs()] },
                { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day'), dayjs()] },
                { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day'), dayjs()] },
                { label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
              ]}
            />
          </Form.Item>

          <Form.Item label="Status">
            <Select
              mode="multiple"
              placeholder="Select status"
              value={filters.status}
              onChange={handleStatusChange}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label="Sort By">
            <Row gutter={8}>
              <Col span={14}>
                <Select
                  placeholder="Select field"
                  value={filters.sortBy}
                  onChange={handleSortChange}
                  options={sortOptions}
                  style={{ width: '100%' }}
                  allowClear
                />
              </Col>
              <Col span={10}>
                <Select
                  value={filters.sortOrder || 'desc'}
                  onChange={handleSortOrderChange}
                  options={[
                    { value: 'desc', label: 'Descending' },
                    { value: 'asc', label: 'Ascending' },
                  ]}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}

/**
 * Compact filter bar for smaller spaces
 */
export function JobFiltersCompact({
  onSearch,
  onStatusChange,
  statusValue,
  loading,
}: {
  onSearch: (value: string) => void;
  onStatusChange: (status: JobStatus | undefined) => void;
  statusValue?: JobStatus;
  loading?: boolean;
}) {
  const [localSearchValue, setLocalSearchValue] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      onSearch(value);
    }, DEBOUNCE.SEARCH);
  };

  const handleSearchNow = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    onSearch(localSearchValue);
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    ...Object.entries(JOB_STATUS_CONFIG).map(([value, config]) => ({
      value,
      label: config.label,
    })),
  ];

  return (
    <Space>
      <Input.Search
        placeholder="Search..."
        value={localSearchValue}
        onChange={(e) => handleSearchChange(e.target.value)}
        onSearch={handleSearchNow}
        onPressEnter={handleSearchNow}
        style={{ width: 200 }}
        allowClear
        loading={loading}
        enterButton
      />
      <Select
        value={statusValue || ''}
        onChange={(value) => onStatusChange(value || undefined)}
        options={statusOptions}
        style={{ width: 120 }}
      />
    </Space>
  );
}

export default JobFilters;
